import { styled } from 'dripsy';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { useModal } from 'react-native-modalfy';
import { useNavigation } from '@react-navigation/native';

import {
  BaseText,
  Icon,
  SettingsLayout,
  Touchable,
} from '@components';
import { modals, ModalStackParamsType } from '@modals';
import { useAuthModel } from '@models/AuthModel';
import { ToastService } from '@services';
import { parseApiError } from '@utils';
import {
  ProfileNavigatorNavigationPropType,
  screens,
  ScreensNameType,
} from '@navigation';
import { useNavOptionsSet } from '@hooks';

const TouchableContainer = styled(Touchable)({
  flexDirection: 'row',
  alignItems: 'center',
  py: '$6',
});

const Line = styled(View)({
  backgroundColor: '$background30',
  height: 1,
  width: '100%',
});

interface ITextStyleProps {
  red?: boolean;
}

const Text = styled(BaseText)((props: ITextStyleProps) => ({
  color: props.red ? '$error300' : '$heading800',
  variant: 'text.medium',
  p: '$5',
}));

export const WebTitle = styled(BaseText)({
  pt: '$5',
  pb: '$4',
  color: '$heading800',
  variant: 'text.xbig',
  fontWeight: 'semi',
});

const BREADCRUMB_ROUTES: ScreensNameType[] = [
  screens.Profile,
  screens.Settings,
  screens.Security,
];

type NavigationPropType =
  ProfileNavigatorNavigationPropType<'Security'>;

export function SecurityScreen() {
  const { t } = useTranslation();
  const { openModal, closeModal } = useModal<ModalStackParamsType>();
  const nav = useNavigation<NavigationPropType>();
  const authModel = useAuthModel();

  useNavOptionsSet({ title: t('security.title') });

  async function deleteAccount() {
    try {
      await authModel.deleteAccount.run();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }

  function openDeleteAccountModal() {
    openModal(modals.ConfirmationModal, {
      title: t('security.deleteAccount'),
      subtitle: t('security.deleteSubtitle'),
      buttons: [
        { text: t('security.delete') },
        { text: t('security.cancel') },
      ],
      onConfirm: () => {
        deleteAccount();
        closeModal();
      },
    });
  }

  function navigateToUpdatePassword() {
    nav.navigate(screens.UpdatePassword);
  }

  return (
    <SettingsLayout
      title={t('security.title')}
      breadcrumbRoutes={BREADCRUMB_ROUTES}
    >
      <TouchableContainer onPress={navigateToUpdatePassword}>
        <Icon name="key-light" size={24} />
        <Text>{t('security.changePassword')}</Text>
      </TouchableContainer>

      <Line />
      <TouchableContainer onPress={openDeleteAccountModal}>
        <Icon name="delete-light" color="$error300" size={24} />
        <Text red>{t('security.deleteAccount')}</Text>
      </TouchableContainer>
    </SettingsLayout>
  );
}
